import { useState } from 'react'
import { useLiveQuery } from 'dexie-react-hooks'
import { useNavigate } from 'react-router-dom'
import { addExpense, db, type ExpenseCategory, type PayMethod } from '../lib/db'
import { clock, money } from '../lib/money'
import { isOwner, useApp } from '../lib/store'
import { pushNow } from '../lib/sync'
import { AppBar } from '../components/AppBar'
import { ExpenseCategoryManager } from '../components/ExpenseCategoryManager'
import { Sheet } from '../components/Sheet'
import { NumPad } from '../components/NumPad'

export function ExpenseScreen() {
  const nav = useNavigate()
  const say = useApp((s) => s.say)
  const refreshCatalog = useApp((s) => s.refreshCatalog)
  const owner = isOwner(useApp((s) => s.session?.role))
  const [picked, setPicked] = useState<ExpenseCategory | null>(null)
  const [amount, setAmount] = useState(0)
  const [note, setNote] = useState('')
  const [method, setMethod] = useState<PayMethod>('Cash')
  const [managing, setManaging] = useState(false)

  const cats = useLiveQuery(() => db.expenseCategories.orderBy('sortOrder').toArray(), [], [])
  const today = useLiveQuery(
    () => db.expenses.where('spentAt').aboveOrEqual(startOfToday()).reverse().sortBy('spentAt'),
    [],
    [],
  )

  const spent = today.reduce((s, e) => s + e.amount, 0)

  function open(cat: ExpenseCategory) {
    setPicked(cat)
    setAmount(0)
    setNote('')
    // Đi chợ mua thịt thì rút tiền trong két ra trả — tiền mặt là mặc định đúng.
    setMethod('Cash')
  }

  async function save() {
    if (!picked || amount <= 0) {
      say('Chưa gõ số tiền')
      return
    }
    await addExpense(
      { id: picked.id, name: picked.name, emoji: picked.emoji },
      amount,
      note || undefined,
      method,
    )
    say(`Đã ghi ${picked.name} ${money(amount)}đ`)
    setPicked(null)
    void pushNow()
  }

  return (
    <div className="screen">
      <AppBar
        title="Nhập hàng"
        sub={spent > 0 ? `Hôm nay chi ${money(spent)}đ` : 'Chưa ghi phiếu nào hôm nay'}
        onBack={() => nav('/')}
        right={
          owner ? (
            <button
              type="button"
              className="icon-btn"
              aria-label="Sửa nút chi phí"
              title="Sửa nút chi phí"
              onClick={() => setManaging(true)}
            >
              🗂
            </button>
          ) : undefined
        }
      />

      <div className="body">
        <p className="hint">Bấm đúng thứ vừa mua, gõ số tiền, xong. Không phải khai kho, không định lượng.</p>

        <div className="expense-grid">
          {cats.map((c) => (
            <button key={c.id} type="button" className="expense-btn" onClick={() => open(c)}>
              <span className="expense-emoji" aria-hidden="true">
                {c.emoji}
              </span>
              <span className="expense-name">{c.name}</span>
            </button>
          ))}
        </div>

        {today.length > 0 && (
          <>
            <h2 className="section-label">Đã ghi hôm nay</h2>
            <div className="list">
              {today.map((e) => (
                <div key={e.id} className="list-row">
                  <span className="list-emoji" aria-hidden="true">
                    {e.emoji}
                  </span>
                  <span className="list-name">
                    {e.categoryName}
                    <small>
                      {clock(e.spentAt)}
                      {e.note ? ` · ${e.note}` : ''}
                    </small>
                  </span>
                  <span className="list-amount out num">
                    {(e.paymentMethod ?? 'Cash') === 'Transfer' && (
                      <span className="pay-mark" title="Chuyển khoản, không rút két">
                        📱{' '}
                      </span>
                    )}
                    −{money(e.amount)}
                  </span>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      {managing && (
        <ExpenseCategoryManager
          categories={cats}
          onClose={() => setManaging(false)}
          onChanged={refreshCatalog}
        />
      )}

      {picked && (
        <Sheet title={`${picked.emoji} ${picked.name}`} onClose={() => setPicked(null)}>
          <p className="sheet-note">Hết bao nhiêu tiền?</p>
          <div className="amount-readout num">{amount ? money(amount) : '0'}</div>

          <div className="chip-row">
            {[50_000, 100_000, 200_000, 500_000].map((v) => (
              <button
                key={v}
                type="button"
                className="chip"
                aria-pressed={amount === v}
                onClick={() => setAmount(v)}
              >
                {money(v)}
              </button>
            ))}
          </div>

          <NumPad value={amount} onChange={setAmount} />

          <div className="field">
            <span>Trả bằng gì</span>
            <div className="seg">
              <button type="button" aria-pressed={method === 'Cash'} onClick={() => setMethod('Cash')}>
                💵 Tiền mặt
              </button>
              <button
                type="button"
                aria-pressed={method === 'Transfer'}
                onClick={() => setMethod('Transfer')}
              >
                📱 Chuyển khoản
              </button>
            </div>
          </div>

          <label className="field">
            <span>Ghi chú</span>
            <input
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="40kg · chợ Bà Chiểu"
              maxLength={200}
            />
          </label>

          <div className="sheet-actions">
            <button type="button" className="btn btn-ghost" onClick={() => setPicked(null)}>
              Bỏ
            </button>
            <button type="button" className="btn btn-brand grow" onClick={save} disabled={amount <= 0}>
              Lưu · {money(amount)}đ
            </button>
          </div>
        </Sheet>
      )}
    </div>
  )
}

function startOfToday(): string {
  const d = new Date()
  d.setHours(0, 0, 0, 0)
  return d.toISOString()
}
